// src/hooks/useTools.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

export const useTools = (adminView = false) => {
  const queryClient = useQueryClient();
  const endpoint = adminView ? '/tools/admin/all' : '/tools';

  const { data: tools = [], isLoading, error, refetch } = useQuery({
    queryKey: ['tools', adminView],
    queryFn: async () => {
      const response = await api.get(endpoint);
      return response.data;
    }
  });

  const createToolMutation = useMutation({
    mutationFn: async (toolData) => {
      const response = await api.post('/tools', toolData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['tools']);
    }
  });

  const updateToolMutation = useMutation({
  mutationFn: async ({ toolId, toolData }) => {
    const response = await api.put(`/tools/${toolId}`, toolData);
    return response.data;
  },
  onSuccess: () => {
    queryClient.invalidateQueries(['tools']);
  }
});

  // Installers go up as multipart, so this one can't reuse the JSON mutations.
  // Content-Type is left alone on purpose — axios has to set the multipart
  // boundary itself.
  const uploadDesktopBuildMutation = useMutation({
    mutationFn: async ({ toolId, platform, file, version, onProgress }) => {
      const body = new FormData();
      body.append('version', version || '');
      body.append('file', file);

      const response = await api.post(`/tools/${toolId}/desktop/${platform}`, body, {
        onUploadProgress: (event) => {
          if (event.total) onProgress?.(Math.round((event.loaded / event.total) * 100));
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['tools']);
    }
  });

  const removeDesktopBuildMutation = useMutation({
    mutationFn: async ({ toolId, platform }) => {
      const response = await api.delete(`/tools/${toolId}/desktop/${platform}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['tools']);
    }
  });

  const uploadToolIconMutation = useMutation({
    mutationFn: async ({ toolId, file }) => {
      const body = new FormData();
      body.append('file', file);
      const response = await api.post(`/tools/${toolId}/icon`, body);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['tools']);
    }
  });

  const removeToolIconMutation = useMutation({
    mutationFn: async ({ toolId }) => {
      const response = await api.delete(`/tools/${toolId}/icon`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['tools']);
    }
  });

  const deleteToolMutation = useMutation({
    mutationFn: async (toolId) => {
      await api.delete(`/tools/${toolId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['tools']);
    }
  });

  return {
    tools,
    isLoading,
    error,
    refetch,
    createTool: createToolMutation.mutateAsync,
    uploadToolIcon: uploadToolIconMutation.mutateAsync,
    removeToolIcon: removeToolIconMutation.mutateAsync,
    uploadDesktopBuild: uploadDesktopBuildMutation.mutateAsync,
    removeDesktopBuild: removeDesktopBuildMutation.mutateAsync,
    updateTool: updateToolMutation.mutateAsync,
    deleteTool: deleteToolMutation.mutateAsync,
    isCreating: createToolMutation.isLoading,
    isUpdating: updateToolMutation.isLoading,
    isDeleting: deleteToolMutation.isLoading
  };
};